import { Badge, Box, Icon, IconButton, Progress, Skeleton, Text } from "@chakra-ui/react";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaTrash } from "react-icons/fa";
import { useImageUploadStatus, useUploadStatusContext } from "./UploadStatusContext";
import { useUploadStatusPoll } from "./useUploadStatusPoll";

type HandlePosition = "top-left" | "top-right" | "bottom-left" | "bottom-right" | "left" | "right" | "top" | "bottom";

export const MAX_IMAGE_WIDTH = 600;

const PLACEHOLDER_ASPECT_RATIO = 4 / 3;
const PLACEHOLDER_WIDTH = MAX_IMAGE_WIDTH;
const PLACEHOLDER_HEIGHT = MAX_IMAGE_WIDTH / PLACEHOLDER_ASPECT_RATIO;

export const ResizableImage = ({ node, updateAttributes, editor, selected, deleteNode }: NodeViewProps) => {
  const { t } = useTranslation();
  const ctx = useUploadStatusContext();
  const uploadId = typeof node.attrs.uploadId === "string" ? node.attrs.uploadId : undefined;
  const feStatus = useImageUploadStatus(uploadId);
  const pollState = useUploadStatusPoll({
    textDocumentId: ctx?.textDocumentId ?? "",
    uploadId: uploadId ?? "",
    skip: !ctx || !uploadId || feStatus?.state !== "processing",
  });

  const imgRef = useRef<HTMLImageElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const currentSrcRef = useRef<string>(node.attrs.src);

  // Reset state synchronously when src changes (before render completes)
  if (node.attrs.src !== currentSrcRef.current) {
    currentSrcRef.current = node.attrs.src;
    setIsLoading(true);
    setHasError(false);
  }

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  // Determine dimensions to use
  const width = node.attrs.width ?? PLACEHOLDER_WIDTH;
  const height = node.attrs.height ?? PLACEHOLDER_HEIGHT;

  const createResizeHandler = useCallback(
    (position: HandlePosition) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = imgRef.current?.offsetWidth ?? 0;
      const startHeight = imgRef.current?.offsetHeight ?? 0;
      const aspectRatio = startWidth / startHeight;

      const isLeft = position.includes("left");
      const isTop = position.includes("top");
      const isHorizontal = position === "left" || position === "right";
      const isVertical = position === "top" || position === "bottom";

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!imgRef.current) return;

        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        let newWidth = startWidth;
        let newHeight = startHeight;

        if (isHorizontal) {
          newWidth = isLeft ? startWidth - deltaX : startWidth + deltaX;
          newHeight = newWidth / aspectRatio;
        } else if (isVertical) {
          newHeight = isTop ? startHeight - deltaY : startHeight + deltaY;
          newWidth = newHeight * aspectRatio;
        } else {
          newWidth = isLeft ? startWidth - deltaX : startWidth + deltaX;
          newHeight = newWidth / aspectRatio;
        }

        newWidth = Math.max(50, newWidth);
        newHeight = Math.max(50, newHeight);

        imgRef.current.style.width = `${newWidth}px`;
        imgRef.current.style.height = `${newHeight}px`;
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);

        if (imgRef.current) {
          updateAttributes({
            width: imgRef.current.offsetWidth,
            height: imgRef.current.offsetHeight,
          });
        }
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [updateAttributes]
  );

  // Derive effective display state
  const isUploading = feStatus?.state === "uploading";
  const isFailed =
    feStatus?.state === "failed" || (feStatus?.state === "processing" && pollState === "failed");
  const isProcessing = feStatus?.state === "processing" && pollState !== "ready" && pollState !== "failed";
  if (isFailed) {
    return (
      <NodeViewWrapper className={`resizable-image-wrapper ${selected ? "selected" : ""}`}>
        <Box
          position="relative"
          display="inline-flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width={`${width}px`}
          height={`${height}px`}
          border="1px solid"
          borderColor="smErrorSoftBorder"
          bg="smErrorSoft"
          color="smError"
          borderRadius="md"
          gap="2"
          p="3"
        >
          <Text fontSize="sm" textAlign="center">
            {t("Couldn't process this image — remove it to continue")}
          </Text>
          <IconButton
            aria-label={t("Remove image")}
            icon={<Icon as={FaTrash} />}
            size="xs"
            colorScheme="red"
            onClick={deleteNode}
          />
        </Box>
      </NodeViewWrapper>
    );
  }

  if (isUploading) {
    const progress = feStatus.state === "uploading" ? feStatus.progress : 0;
    return (
      <NodeViewWrapper className={`resizable-image-wrapper ${selected ? "selected" : ""}`}>
        <Box position="relative" display="inline-block">
          <Skeleton width={`${width}px`} height={`${height}px`} borderRadius="md" />
          <Progress
            role="progressbar"
            value={progress}
            size="xs"
            colorScheme="teal"
            position="absolute"
            bottom="0"
            left="0"
            right="0"
            borderBottomRadius="md"
          />
        </Box>
      </NodeViewWrapper>
    );
  }

  if (isProcessing) {
    return (
      <NodeViewWrapper className={`resizable-image-wrapper ${selected ? "selected" : ""}`}>
        <Box position="relative" display="inline-block">
          <Skeleton width={`${width}px`} height={`${height}px`} borderRadius="md" />
          <Badge position="absolute" top="1" left="1" colorScheme="teal" fontSize="xs">
            {t("Processing…")}
          </Badge>
        </Box>
      </NodeViewWrapper>
    );
  }

  // Ready / no status — existing behavior
  const showHandles = editor.isEditable && selected;
  const showSkeleton = isLoading || hasError;

  return (
    <NodeViewWrapper className={`resizable-image-wrapper ${selected ? "selected" : ""}`}>
      <Box position="relative" display="inline-block">
        {showSkeleton && (
          <Skeleton
            width={`${width}px`}
            height={`${height}px`}
            borderRadius="md"
          />
        )}
        <img
          ref={imgRef}
          key={node.attrs.src}
          src={node.attrs.src}
          alt={node.attrs.alt ?? ""}
          title={node.attrs.title ?? ""}
          draggable={false}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            display: showSkeleton ? "none" : "block",
            width: node.attrs.width ? `${node.attrs.width}px` : undefined,
            height: node.attrs.height ? `${node.attrs.height}px` : undefined,
            maxWidth: node.attrs.width ? undefined : `${MAX_IMAGE_WIDTH}px`,
          }}
        />
        {showHandles && !showSkeleton && (
          <>
            <IconButton
              aria-label={t("Remove image")}
              icon={<Icon as={FaTrash} />}
              size="xs"
              colorScheme="gray"
              position="absolute"
              top="1"
              right="1"
              zIndex="1"
              onClick={deleteNode}
            />
            <div className="resize-zone corner top-left" onMouseDown={createResizeHandler("top-left")} />
            <div className="resize-zone corner top-right" onMouseDown={createResizeHandler("top-right")} />
            <div className="resize-zone corner bottom-left" onMouseDown={createResizeHandler("bottom-left")} />
            <div className="resize-zone corner bottom-right" onMouseDown={createResizeHandler("bottom-right")} />
            <div className="resize-zone edge top" onMouseDown={createResizeHandler("top")} />
            <div className="resize-zone edge bottom" onMouseDown={createResizeHandler("bottom")} />
            <div className="resize-zone edge left" onMouseDown={createResizeHandler("left")} />
            <div className="resize-zone edge right" onMouseDown={createResizeHandler("right")} />
          </>
        )}
      </Box>
    </NodeViewWrapper>
  );
};
